/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import { useFormContext } from '../../../../../contexts/FormContext';
import { Campaign } from '../../../../../types/Campaign.type';
import {
  AllWebsiteInputs,
  BlogInput,
  BrandMissionInput,
  EmailInput,
  FormType,
  PersonaBlogInput,
  PersonaEmailInput,
  PersonaHeroHeaderInput,
  PersonaSocialInput,
  SocialInput,
  ToneChangerInput,
  YoutubeVideoScriptInput,
  ShortVideoScriptInput,
  LinkedInCompanyBioInput,
  ValuePropInput,
} from '../../../../../types/FormTypes';
import { NextImageBasicData } from '../../../../../types/Next';
import { BasicButtonSelectionInput } from '../../../../shared/inputs/BasicButtonSelectionInput';
import { SliderInput } from '../../../../shared/inputs/SliderInput';
import {
  blogDefaultToneOptions,
  socialDefaultPlatformIcons,
  suggestedTones,
} from './DefaultOptions';

import styles from './ToneInputs.module.css';
import { PersonaHeroHeaderForm } from '../website/PersonaHeroHeaderForm';
import { PersonaSubheaderInput } from '../../../../../types/FormTypes';
import { PersonaCallToActionInput } from '../../../../../types/FormTypes';
import { PersonaFeatureDescriptionInput } from '../../../../../types/FormTypes';

type ToneInputsProps<T> = {
  formikProps: FormikProps<T>;
  disabled: boolean;
};

export function ToneFineTuning<
  T extends
    | BlogInput
    | PersonaBlogInput
    | SocialInput
    | EmailInput
    | PersonaEmailInput
    | PersonaSocialInput
    | PersonaHeroHeaderInput
    | PersonaSubheaderInput
    | PersonaCallToActionInput
    | PersonaFeatureDescriptionInput
    | BrandMissionInput
    | YoutubeVideoScriptInput
    | ShortVideoScriptInput
    | LinkedInCompanyBioInput
    | ValuePropInput
>({ formikProps, disabled }: ToneInputsProps<T>) {
  if (
    Array.isArray(formikProps.values.tone) &&
    formikProps.values.tone.filter((tone) => !tone.includes('suggested'))
      .length === 0
  ) {
    return null;
  }
  if (
    formikProps.values.tone.length !== 0 &&
    formikProps.values.tone.length === 1 &&
    formikProps.values.tone[0] === ''
  ) {
    return null;
  }

  return (
    <div aria-label="toneFineTuning-input">
      <h2>Fine-tune tone of voice</h2>
      <div className={styles['fine-tuning-sliders-container']}>
        {Array.isArray(formikProps.values.tone) &&
          formikProps.values.tone.length !== 0 &&
          formikProps.values.tone.length === 1 &&
          formikProps.values.tone[0] !== '' &&
          formikProps.values.tone
            .filter((tone) => !tone.includes('suggested'))
            .map((tone, index) => {
              const toneDegree = formikProps.values.toneDegree[tone] || '50';
              return (
                <SliderInput
                  key={`${tone}-${index}`}
                  formikProps={formikProps}
                  disabled={disabled}
                  label={
                    <p>
                      <b>{tone}</b>
                    </p>
                  }
                  valueKey={'toneDegree'}
                  value={toneDegree}
                  onChange={(e) => {
                    const newToneDegree = formikProps.values.toneDegree;
                    newToneDegree[tone] = e.target.value;
                    formikProps.setFieldValue('toneDegree', newToneDegree);
                  }}
                  step="50"
                  rangeLabels={['Kinda', 'Somewhat', 'Very']}
                />
              );
            })}
      </div>
    </div>
  );
}

export function ToneInputs<
  T extends
    | BlogInput
    | SocialInput
    | EmailInput
    | PersonaEmailInput
    | Campaign
    | ToneChangerInput
    | AllWebsiteInputs
>({ formikProps, disabled }: ToneInputsProps<T>) {
  const { formType } = useFormContext();

  const [defaultToneOptions, setDefaultToneOptions] = useState(
    blogDefaultToneOptions
  );
  const [defaultSocialIcon, setDefaultSocialIcon] = useState<
    NextImageBasicData[]
  >([]);
  const [prevTone, setPrevTone] = useState<string[]>(formikProps.values.tone);

  useEffect(() => {
    let suggestedToneOption: string | undefined;
    let suggestedSocialIcon: NextImageBasicData[] = [];
    const castedSocialInput = formikProps.values as SocialInput;

    if (castedSocialInput.platform) {
      suggestedToneOption =
        suggestedTones[FormType.SOCIAL][castedSocialInput.platform];
      suggestedSocialIcon = [
        socialDefaultPlatformIcons[castedSocialInput.platform],
      ];
    }

    if (suggestedToneOption) {
      setDefaultToneOptions([suggestedToneOption, ...blogDefaultToneOptions]);
    } else {
      setDefaultToneOptions([...blogDefaultToneOptions]);
    }

    setDefaultSocialIcon(suggestedSocialIcon);
  }, [formType, formikProps]);

  // If suggested tone is selected, it can be the only tone selected
  useEffect(() => {
    if (formikProps.values.tone.length > 1) {
      const prevSuggestedIndex = prevTone.findIndex((tone) =>
        tone.includes('suggested')
      );
      const newSuggestedIndex = formikProps.values.tone.findIndex((tone) =>
        tone.includes('suggested')
      );
      // Suggested tone has been newly selected
      if (newSuggestedIndex > -1 && prevSuggestedIndex === -1) {
        formikProps.setFieldValue('tone', [
          formikProps.values.tone[newSuggestedIndex],
        ]);
      } else if (prevSuggestedIndex > -1 && newSuggestedIndex > -1) {
        // Other tones were selected in addition to the suggested tone
        const newTone = [...formikProps.values.tone];
        newTone.splice(newSuggestedIndex, 1);
        formikProps.setFieldValue('tone', newTone);
      }
    }
    setPrevTone(formikProps.values.tone);
  }, [formikProps.values.tone]);

  return (
    <div>
      <BasicButtonSelectionInput
        isMultipleSelection
        allowUserInput
        id="tone"
        label={
          <div>
            <h2>What tone of voice would you like to apply?</h2>
            <p>Select all that apply</p>
          </div>
        }
        defaultOptions={defaultToneOptions}
        defaultOptionIcons={defaultSocialIcon}
        value={formikProps.values.tone}
        disabled={disabled}
        errors={formikProps.errors.tone as string}
        touched={formikProps.touched.tone ? true : false}
        onBlur={formikProps.handleBlur}
      />
    </div>
  );
}
